<script lang="ts">
  import FaPlusSquare from "svelte-icons/fa/FaPlusSquare.svelte";
  import FaBars from 'svelte-icons/fa/FaBars.svelte';
  import FaSortDown from "svelte-icons/fa/FaSortDown.svelte";
  import FaSignOutAlt from 'svelte-icons/fa/FaSignOutAlt.svelte'
  import FaInfoCircle from 'svelte-icons/fa/FaInfoCircle.svelte';
  import IoMdCheckmark from 'svelte-icons/io/IoMdCheckmark.svelte';
  import MdLanguage from 'svelte-icons/md/MdLanguage.svelte';
  import FaDatabase from 'svelte-icons/fa/FaDatabase.svelte'
  import FaExpand from 'svelte-icons/fa/FaExpand.svelte';
  import IoMdClose from 'svelte-icons/io/IoMdClose.svelte';
  import FaTimes from 'svelte-icons/fa/FaTimes.svelte'
  import FaLinkedin from 'svelte-icons/fa/FaLinkedin.svelte'
  import FaTwitter from 'svelte-icons/fa/FaTwitter.svelte'
  import FaFacebookF from 'svelte-icons/fa/FaFacebookF.svelte'
  import visitoryLogoUrl from "./images/logo-visitory.svg";
  import visitoryBlackLogoUrl from "./images/logo-visitory-black.svg";
  import { TimeSeriesChart } from "./data-visualization/charts/highcharts-time-series";
  import { VisitorySource } from "./data-sources/visitory-source";
  import TableView from "./data-views/table/table-view.svelte";
  import ChartView from "./data-views/chart/chart-view.svelte";
  import FigureView from "./data-views/figure/figure-view.svelte";
  import { visualizationDataStore, showHeaderStore, dictionaryStore } from "./data-views/stores";
  import { Languages, Visualizations, languageMapping } from "./data-views/options";
  import IconDropdown from "./ui-components/icon-dropdown.svelte";
  import ViewsDropdown from "./ui-components/views-dropdown.svelte";
  import VisualizationSelector from "./ui-components/visualization-selector.svelte";
  import ToggleSwitch from "./ui-components/toggle-switch.svelte";
  import FeedbackDrawer from "./ui-components/feedback-drawer.svelte";
  import FloatingButton from "./ui-components/floating-button.svelte";
  import { horizontalSlide } from './ui-components/transitions';
  import { onMount, tick } from "svelte";
  import { fade } from 'svelte/transition';
  import { t, locale } from "./translations/i18n";
  import { trackDashboardEnter, trackVisualizationCreated } from "./utils/analytics";

  const visitorySource = new VisitorySource();

  // Store the visualization data in the store so the data is not lost when the page is reloaded
  let visualizationDataCollection: any[] = [];

  let resetConfirmation = false;

  let sideBarOpen = false;

  let dictOpen = false;
  let dictionaryExpanded = false;
  let searchQuery = "";
  let searchTokens: string[] = [];
  let dictionaryEntries: any[] = [];
  let dictionaryLoading = false;

  let _overlayTouchY = 0;


  // Forward wheel scroll to window so underlying page can scroll
  function overlayWheel(e: WheelEvent) {
    if (typeof window !== 'undefined') {
      window.scrollBy({ top: e.deltaY, behavior: 'auto' });
    }
  }

  // Forward touch scroll to window so underlying page can scroll
  function overlayTouchStart(e: TouchEvent) {
    if (e.touches && e.touches.length) {
      _overlayTouchY = e.touches[0].clientY;
    }
  }
  function overlayTouchMove(e: TouchEvent) {
    if (e.touches && e.touches.length) {
      const currentY = e.touches[0].clientY;
      const delta = _overlayTouchY - currentY;
      if (typeof window !== 'undefined') {
        window.scrollBy({ top: delta, behavior: 'auto' });
      }
      _overlayTouchY = currentY;
    }
  }

  // Get current language code
  $: currentLang = $locale.split('-')[0];

  dictionaryStore.subscribe((state) => {
    dictOpen = state.open;
    searchQuery = state.search;
  });

  const handleDictionarySearchInput = (event: Event) => {
    const input = event.target as HTMLInputElement;
    dictionaryStore.setSearch(input.value);
  };


  const normalizeDictionaryString = (raw: string): string => {
    // Unicode-aware: keep letters, numbers and spaces
    try {
      return String(raw)
        .toLowerCase()
        .replace(/[^\p{L}\p{N}\s]/gu, "")
        .replace(/\s+/g, " ")
        .trim();
    } catch (e) {
      // Fallback when Unicode property escapes unsupported
      return String(raw)
        .toLowerCase()
        .replace(/[^\w\s]|_/g, "")
        .replace(/\s+/g, " ")
        .trim();
    }
  };

  // Split dictionary search string into tokens
  const getDictionaryQueryTokens = (query: string) =>
    normalizeDictionaryString(query)
      .split(" ")
      .filter(Boolean);

  $: searchTokens = getDictionaryQueryTokens(searchQuery || "");

  /*
    Function to highlight matching token ranges within a text string 
  */
  const getHighlightParts = (text: string, tokens: string[]) => {
    if (!text || tokens.length === 0) {
      return [{ text, matched: false }];
    }

    const raw = String(text);
    const normalizedChars: string[] = [];
    const mapping: number[] = [];

    for (let i = 0; i < raw.length; i += 1) {
      const normalizedChar = normalizeDictionaryString(raw[i]);
      if (normalizedChar.length === 0) continue;
      normalizedChars.push(normalizedChar);
      mapping.push(i);
    }

    const normalizedText = normalizedChars.join("");
    const ranges: [number, number][] = [];

    for (const token of tokens) {
      let start = 0;
      while (true) {
        const tokenIndex = normalizedText.indexOf(token, start);
        if (tokenIndex < 0) break;
        ranges.push([tokenIndex, tokenIndex + token.length]);
        start = tokenIndex + token.length;
      }
    }

    if (ranges.length === 0) {
      return [{ text, matched: false }];
    }

    ranges.sort((a, b) => a[0] - b[0]);
    const merged: [number, number][] = [ranges[0]];
    for (let i = 1; i < ranges.length; i += 1) {
      const [start, end] = ranges[i];
      const last = merged[merged.length - 1];
      if (start <= last[1]) {
        last[1] = Math.max(last[1], end);
      } else {
        merged.push([start, end]);
      }
    }

    const parts: Array<{ text: string; matched: boolean }> = [];
    let cursor = 0;

    for (const [start, end] of merged) {
      const origStart = mapping[start];
      const origEnd = mapping[end - 1] + 1;
      if (cursor < origStart) {
        parts.push({ text: raw.slice(cursor, origStart), matched: false });
      }
      parts.push({ text: raw.slice(origStart, origEnd), matched: true });
      cursor = origEnd;
    }

    if (cursor < raw.length) {
      parts.push({ text: raw.slice(cursor), matched: false });
    }

    return parts;
  };

  /*
    Function to extract searchable text chunks from a dictionary entry (concept, definition, keywords) 
  */
  const getEntryChunks = (entry: any) => {
    const chunks: { text: string; original: string; isKeyword: boolean; isConcept: boolean }[] = [];
    const currentConceptKey = `concept_${currentLang}`;
    const currentDefinitionKey = `definition_${currentLang}`;

    if (Object.prototype.hasOwnProperty.call(entry, currentConceptKey)) {
      const orig = String(entry[currentConceptKey] || "");
      chunks.push({ text: normalizeDictionaryString(orig), original: orig, isKeyword: false, isConcept: true });
    }

    if (Object.prototype.hasOwnProperty.call(entry, currentDefinitionKey)) {
      const orig = String(entry[currentDefinitionKey] || "");
      chunks.push({ text: normalizeDictionaryString(orig), original: orig, isKeyword: false, isConcept: false });
    }

    if (entry.keywords) {
        entry.keywords.split(/\r?\n/).forEach((line: string) => {
          const orig = String(line || "");
          chunks.push({ text: normalizeDictionaryString(orig), original: orig, isKeyword: true, isConcept: false });
        });
    }

    return chunks.filter((chunk) => chunk.text.length > 0);
  };

  /*
    Function to score a dictionary entry against search tokens and return match info
   */
  const getEntryMatchInfo = (entry: any, tokens: string[], normalizedQuery: string): { score: number; matchedKeywords?: string[]; hasNonKeywordMatch?: boolean } => {
    const chunks = getEntryChunks(entry);
    let bestScore = 0;
    const matchedKeywords: string[] = [];
    let hasNonKeywordMatch = false;

    for (const chunk of chunks) {
      const text = chunk.text;
      if (!text) continue;

      if (normalizedQuery && text.includes(normalizedQuery)) {
        const chunkScore = chunk.isConcept ? 1400 : chunk.isKeyword ? 1200 : 1000;
        bestScore = Math.max(bestScore, chunkScore);
        if (chunk.isKeyword) matchedKeywords.push(chunk.original);
        else hasNonKeywordMatch = true;
        continue;
      }

      const hasAllTokens = tokens.every((token) => text.includes(token));
      if (!hasAllTokens) continue;

      let score = chunk.isConcept ? 300 : chunk.isKeyword ? 250 : 150;
      let position = 0;
      let ordered = true;
      for (const token of tokens) {
        const index = text.indexOf(token, position);
        if (index >= 0) {
          position = index + token.length;
        } else {
          ordered = false;
          break;
        }
      }
      if (ordered) score += 40;

      bestScore = Math.max(bestScore, score);
      if (chunk.isKeyword && score > 0) matchedKeywords.push(chunk.original);
      if (!chunk.isKeyword && score > 0) hasNonKeywordMatch = true;
    }

    return { score: bestScore, matchedKeywords, hasNonKeywordMatch };
  };

  const openDictionaryHandler: any = () => {
    if ($dictionaryStore.open) {
      dictionaryStore.closeDictionary();
      dictionaryExpanded = false;
    } else {
      dictionaryStore.openDictionary();
    }
  };

  const toggleDictionaryExpanded = () => {
    dictionaryExpanded = !dictionaryExpanded;
  };

  // Filter and sort dictionary entries by match score when search query exists
  $: filteredEntries = (() => {
    const query = searchQuery || "";
    const queryTokens = getDictionaryQueryTokens(query);
    const normalizedQuery = normalizeDictionaryString(query);
    if (!queryTokens.length) return dictionaryEntries.map((entry) => ({ entry, score: 0, matchedKeywords: [], hasNonKeywordMatch: false }));

    return dictionaryEntries
      .map((entry) => {
        const info = getEntryMatchInfo(entry, queryTokens, normalizedQuery);
        return { entry, score: info.score, matchedKeywords: info.matchedKeywords || [], hasNonKeywordMatch: info.hasNonKeywordMatch || false };
      })
      .filter(({ score }) => score > 0)
      .sort((a, b) => b.score - a.score);
  })();

  // Variables for controlling the sublists in the side menu
  let showSublist = {
    tourismresearch: false,
    help: false
  };

  // Variable for controlling the presentation mode (hide visualization headers)
  let hideVisualizationHeaders = false;
  $: handlePresentationMode(hideVisualizationHeaders);

  // Variables for controlling the focus on the elements for keyboard navigation
  let focusedFooterElementIndex = 0;
  let navbarFocused = false;
  let sideMenuFocused = false;
  let footerFocused = false;

  // Variable for controlling the feedback drawer (bottom right corner)
  let showFeedbackDrawer = false;

  function handleToggleDrawer() {
    showFeedbackDrawer = !showFeedbackDrawer;
  }

  // Subscribe to the store to get the visualization data
  visualizationDataStore.subscribe((data) => {
    visualizationDataCollection = data;
  });

  /**
   * Function for adding a new visualization to the page
   * 
   * @param visualization - The visualization type (right now only chart, table, figure)
  */
  const addVisualizationHandler: any = (visualization: string) => {
    // Track visualization creation
    trackVisualizationCreated(visualization);

    // Visualization selector which is shown when there are no visualizations
    let visualization_selector = document.querySelector(".visualization-selector");

    // Set the display to none
    if (visualization_selector) {
      (visualization_selector as HTMLElement).style.display= "none";
    }

    // Get the container for the visualizations
    const target = document.querySelector("main .visualizations");
    if (!target) throw new Error("Container for visualizations not found");

    switch (visualization) {
      case Visualizations.CHART:
        new ChartView({
          target,
          props: {
            chart: new TimeSeriesChart(),
            dataSource: visitorySource,
            showSelectorButtons: false,
            filterSelectProps: [],
          },
        });
        break;
      case Visualizations.TABLE:
        new TableView({
          target,
          props: {
            dataSource: visitorySource,
            showSelectorButtons: false,
          },
        });
        break;
      case Visualizations.FIGURE:
        new FigureView({
          target,
          props: {
            dataSource: visitorySource,
            showSelectorButtons: false,
          },
        });
        break;
    }
  };

  /**
   * Function for removing all visualizations and reloading the page
   */
  const confirmReset = () => {
    visualizationDataStore.resetStore();
    location.reload();
  };

  const cancelReset = () => {
    resetConfirmation = false;
  };

  /**
   * Function for creating the visualization selector when there are no visualizations
   */
   const createVisualizationSelector = () => {
    // Get the container for the visualizations
    const target = document.querySelector("main .visualizations");
    if (!target) throw new Error("Container for visualizations not found");

    // Create VisualizationSelector when there are no visualizations
    new VisualizationSelector({
      target,
      props: {
        handler: addVisualizationHandler,
      }
    });
  };

  /**
   * Function for restoring the previous visualizations from the visualizationDataCollection (local storage)
   */
  const restorePreviousVisualizations = () => {
    const target = document.querySelector("main .visualizations");
    if (!target) throw new Error("Container for visualizations not found");

    // Loop through the visualization data collection and create the visualizations
    for (let i = 0; i < visualizationDataCollection.length; i++) {
        const visualization = visualizationDataCollection[i];
        switch (visualization.type) {
          case Visualizations.CHART:
            new ChartView({
              target,
              props: {
                chart: new TimeSeriesChart(),
                dataSource: visitorySource,
                showSelectorButtons: false,
                filterSelectProps: [],
                restoreView: true,
                storageData: visualization,
              },
            });
            break;
          case Visualizations.TABLE:
            new TableView({
              target,
              props: {
                dataSource: visitorySource,
                showSelectorButtons: false,
                restoreView: true,
                storageData: visualization,
              },
            });
            break;
          case Visualizations.FIGURE:
            new FigureView({
              target,
              props: {
                dataSource: visitorySource,
                showSelectorButtons: false,
                restoreView: true,
                storageData: visualization,
              },
            });
            break;
        }
      }
  };

  /**
   * Function for loading the view from the event
   * 
   * @param event - The event with the view data
   */
  const loadView = (event: any) => {
    // Get the view data from the event and overwrite the local storage
    if (!event.detail["view"]) return;
    visualizationDataCollection = event.detail["view"];
    // Set the new data to the store
    visualizationDataStore.set(visualizationDataCollection);
    location.reload();
  }


  /**
   * Function for changing the language
   * @param language - 
   */
  const changeLanguageHandler = async (language: any) => {

    // Ensure we're working with a single string
    const selectedLanguage = Array.isArray(language) ? language[0] : language;

    // Get the language code from the language mapping
    const locale_language = languageMapping[selectedLanguage];

    if (locale_language) {
      // Set the language cookie
      document.cookie = `visitory_language=${locale_language.split('-')[0]}; path=/; max-age=Session; SameSite=None; Secure;`;
    }
    locale.set(locale_language);

    // Redirect to the link with the new language
    window.location.href = document.URL.replace(/(en|fi|sv|nb)/, locale_language.split('-')[0]);
  };

  /** 
   * Function for opening the side menu
   */
  const openSideMenu = () => {
    sideBarOpen = true;
    sideMenuFocused = true;
    const mainView = document.getElementsByTagName("main")[0];
    mainView.style.overflow = "hidden"
    mainView.style.height = "100vh";
  };

  /**
   * Function for closing the side menu
   */
  const closeSideMenu = () => {
    sideBarOpen = false;
    navbarFocused = true;
    sideMenuFocused = false;
    showSublist = {
      tourismresearch: false,
      help: false
    };

    const mainView = document.getElementsByTagName("main")[0];
    mainView.style.removeProperty("overflow");
    mainView.style.height = "auto";
  };

  /**
   * Function for toggling the sublist in the side menu
   * 
   * @param tag_id - The tag id of the sublist
   */
  const toggleSublist = (tag_id: keyof typeof showSublist) => {
    return (event: any) => {
      event.preventDefault();
      showSublist = {
        ...showSublist,
        [tag_id]: !showSublist[tag_id]
      };
    };
  };

  /**
   * Function for handling the presentation mode (hide/show visualization headers)
   * 
   * @param switchState - The state of the switch
   */
  const handlePresentationMode = (switchState: boolean) => {
    if (switchState) {
      // Hide headers
      showHeaderStore.set(false);
    } else {
      // Show headers
      showHeaderStore.set(true);
    }
  }

  onMount(async () => {
    // Track dashboard entry event
    trackDashboardEnter();

    if (visualizationDataCollection.length === 0) {
      createVisualizationSelector();
    } else {
      restorePreviousVisualizations();
    }

    // Fetch dictionary entries from API
    await fetchDictionary();
  });

  /**
   * Fetch dictionary entries from the API
   */
  async function fetchDictionary() {
    try {
      dictionaryLoading = true;
      dictionaryEntries = await visitorySource.getDictionary();
      dictionaryStore.setEntries(dictionaryEntries);
    } catch (error) {
      console.error('Error fetching dictionary:', error);
      dictionaryEntries = [];
      dictionaryStore.setEntries([]);
    } finally {
      dictionaryLoading = false;
    }
  }

  /**
   * Handle keyboard events
   * @param event - The keyboard event
   * @returns void
  */
  const onKeyDown = async (event: KeyboardEvent) => {
    // Footer: get all elements
    let footerElements = document.querySelectorAll('#footer-content li a, #footer-icons a');
    focusedFooterElementIndex = Array.from(footerElements).findIndex((element: Element) => 
      element === document.activeElement || element.contains(document.activeElement)
    );

    // Navbar: Get all elements
    let navbarElements = [
      ...Array.from(document.querySelectorAll('[id^="navbar-"]'))
        .map((element: Element) => element.firstElementChild)
        .filter((element): element is Element => element !== null),
      ...(document.getElementById("app-logout")?.firstChild ? [document.getElementById("app-logout")] : [])
    ];

    // Find the index of the currently focused element from navbarElements
    let focusedElementIndex = navbarElements.findIndex((element: Element | null) => 
      element === document.activeElement || (element && element.contains(document.activeElement))
    );

    const isNavbarFocused = (element: any) => {
      if (resetConfirmation) {
        return false;
      } else {
        return element?.id === "navbar" || navbarElements[focusedElementIndex] === element;
      }
    };

    const isFooterFocused = (element: any) => {
      return element?.id === "footer-content" ||
         footerElements[focusedFooterElementIndex] === element;
    };

    navbarFocused = isNavbarFocused(document.activeElement);
    footerFocused = isFooterFocused(document.activeElement);

    if (navbarFocused) {
      if (focusedElementIndex === -1) {
        focusedElementIndex = 0;
      }
    }

    // Side Menu: get all li elements
    let sideMenuElements = document.querySelectorAll('#side-menu li a, #sidemenu-icons a, #sidemenu-logout, #sidemenu-language-selector > :first-child');
    if (sideBarOpen) {
      // Find the index of the currently focused element
      focusedElementIndex = Array.from(sideMenuElements).findIndex((element: Element) => 
        element === document.activeElement || element.contains(document.activeElement)
      );
      // Open the sublist when the focus is on the side menu
      showSublist = {
        tourismresearch: true,
        help: true
      };
    }

    switch (event.key) {
      case "Escape":
        if (!sideMenuFocused && sideBarOpen) {
          // Just close the dropdown, when the side menu and language dropdown are open
          sideMenuFocused = true;
          (document.getElementById("sidemenu-language-selector")?.firstChild as HTMLElement)?.focus();
        } else if (resetConfirmation) {
          // Cancel the reset confirmation
          cancelReset();
          // Update DOM
          await tick();
          // Set the focus to the reset button
          navbarFocused = true;
          (document.getElementById("reset-button") as HTMLElement)?.focus();
        } else {
          // Close the side menu
          closeSideMenu();
        }
        break;

      case "Enter":
        let focusedElement = document.activeElement as HTMLElement;
        focusedElement?.click();
        // Set the focus to the dropdown icon when the dropdown and side menu are open
        if (focusedElement?.id.includes("icon-dropdown-") && sideBarOpen) {
          sideMenuFocused = false;
        }
        if (resetConfirmation) {
          await tick();
          // Change the focus to the cancel button
          navbarFocused = false;
          (document.getElementById("reset-page-cancel") as HTMLElement)?.focus();
        }
        if (focusedElement?.id === "reset-page-cancel") {
          await tick();
          navbarFocused = true;
          (document.getElementById("reset-button") as HTMLElement)?.focus();
        }
        break;

      case "ArrowRight":
        if (navbarFocused) {
          // Focus the next element
          if (focusedElementIndex < navbarElements.length - 1) {
            ((navbarElements[focusedElementIndex + 1] as HTMLElement) as HTMLElement)?.focus();
          }
        } else if (resetConfirmation) {
          // Change the focus to the cancel button
          (document.getElementById("reset-page-cancel") as HTMLElement)?.focus();
        } else if (footerFocused) {
          // Focus the next element
          (footerElements[focusedFooterElementIndex + 1] as HTMLElement)?.focus();
        }
        break;

      case "ArrowLeft":
        if (navbarFocused) {
          // Focus the previous element
          if (focusedElementIndex > 0) {
            ((navbarElements[focusedElementIndex - 1] as HTMLElement) as HTMLElement)?.focus();
          }
        }
        else if (resetConfirmation) {
          // Change the focus to the reset button
          (document.getElementById("reset-page-confirm") as HTMLElement)?.focus();
        }
        else if (footerFocused) {
          // Focus the previous element
          (footerElements[focusedFooterElementIndex - 1] as HTMLElement)?.focus();
        }
        break;
      
      case "ArrowDown":
        if (sideMenuFocused) {
          // Focus the next element
          if (focusedElementIndex < sideMenuElements.length - 1) {
            (sideMenuElements[focusedElementIndex + 1] as HTMLElement).focus();
          }
          else {
            // If the last element is focused, focus the first element
            (sideMenuElements[0] as HTMLElement).focus();
          }
        }
        if (footerFocused) {
          // Focus the first footer element
          (footerElements[0] as HTMLElement)?.focus();
        }
        break;
      
      case "ArrowUp":
        if (sideMenuFocused) {
          // Focus the previous element
          if (focusedElementIndex > 0) {
            (sideMenuElements[focusedElementIndex - 1] as HTMLElement).focus();
          }
          else {
            // If the first element is focused, focus the last element
            (sideMenuElements[sideMenuElements.length - 1] as HTMLElement).focus();
          }
        }
        break;

      default:
        // Allow default behavior for other keys
        return;
    }

    // Prevent default behavior only for relevant keys
    event.preventDefault();
  };
  
</script>

<!-- svelte-ignore a11y-no-noninteractive-tabindex a11y-click-events-have-key-events -->
{#if sideBarOpen}
<div class="gray-screen" transition:fade={{ duration: 250 }} on:click={closeSideMenu}></div>

<div id="side-menu" transition:horizontalSlide={{axis: 'x', duration: 250}} tabindex="0" on:keydown={onKeyDown}>
  <div class="close-icon-wrapper" on:click={closeSideMenu}>
    <FaTimes />
  </div>
  
  <ul class="side-list">
    <li>
      <a href="https://visitory.io/{($locale.split('-')[0])}/">{$t("Home")}</a>
    </li>
    <li>
      <a href="https://visitory.io/{($locale.split('-')[0])}/about/">{$t("About")}</a>
    </li>
    <li>
      <button id="tourismresearch" class="sublist_button" on:click|preventDefault={toggleSublist("tourismresearch")}>
        {$t("Tourism research")}
        <div class="icon-down"><FaSortDown /></div>
      </button>
      <ul class="sublist {showSublist.tourismresearch ? 'open' : ''}">
        <li><a href="https://www.insights.visitory.io/{($locale.split('-')[0])}/tourismresearch">{$t("Foreign Tourists")}</a></li>
        <li><a href="https://www.insights.visitory.io/{($locale.split('-')[0])}/tourismincome">{$t("Tourism Employment & Income")}</a></li>
        <li><a href="https://www.insights.visitory.io/{($locale.split('-')[0])}/account/travelanalytics">{$t("Travel Analytics")}</a></li>
      </ul>
    </li>
    <li>
      <a href="https://www.insights.visitory.io/{($locale.split('-')[0])}">{$t("Blog")}</a>
    </li>
    <li>
      <button id="help" class="sublist_button" on:click|preventDefault={toggleSublist("help")}>
        {$t("Help")}
        <div class="icon-down"><FaSortDown /></div>
      </button>
      <ul class="sublist {showSublist.help ? 'open' : ''}">
        <li><a href="https://www.insights.visitory.io/{($locale.split('-')[0])}/tourismresearch">{$t("FAQ")}</a></li>
        <li><a href="https://www.insights.visitory.io/{($locale.split('-')[0])}/dictionary">{$t("Dictionary")}</a></li>
      </ul>
    </li>
    <li>
      <a href="https://visitory.io/{($locale.split('-')[0])}/contact/">{$t("Contact")}</a>
    </li>
  </ul>

  <div class="presentation-mode-side-menu">
    <ToggleSwitch bind:checked={hideVisualizationHeaders}>
      <p class="presentation-switch-text" slot="switch-text">{$t("Presentation mode")}</p>
    </ToggleSwitch>
  </div>

  {#if resetConfirmation}
    <div class="reset-container side-menu">
      <p id="reset-text">{$t("Reset page?")}</p>
      <div class="reset-wrapper" id="navbar-reset-wrapper-confirmation" tabindex="0">
        <div class="confirm" id="reset-page-confirm" tabindex="0" on:click={confirmReset}><IoMdCheckmark/></div>
        <div class="cancel" id="reset-page-cancel" tabindex="0" on:click={cancelReset}><IoMdClose/></div>
      </div>
    </div>
    {:else}
    <div class="reset-container side-menu">
      <div class="reset-wrapper" id="navbar-reset-wrapper-button">
        <button class="reset-button" id="reset-button" on:click={() => {resetConfirmation = true}} title="{$t("Reset page")}">{$t("Reset page")}</button>
      </div>
    </div>
    {/if}

  <div class="language-selector" id="sidemenu-language-selector">
    <IconDropdown
      options={Languages}
      handler={changeLanguageHandler}
      title="Language"
    >
      <div class="change-language" slot="icon" id="change-language">
        <div class="icon">
          <MdLanguage />
        </div>
          {Languages[$locale]}
        <div class="icon-down">
          <FaSortDown />
        </div>
      </div>
    </IconDropdown>
  </div>

  <a href="https://visitory.io/{($locale.split('-')[0])}/account/logout/" class="logout" id="sidemenu-logout">
    <div class="icon-wrapper">
      <FaSignOutAlt/>
    </div>
    {$t("Logout")}
  </a>

  <div class="side-menu-icons" id="sidemenu-icons">
    <a href="https://www.linkedin.com/showcase/visitory" class="icon"><FaLinkedin/></a>
    <a href="https://twitter.com/visitory_io/" class="icon"><FaTwitter/></a>
    <a href="https://www.facebook.com/Visitory-100600242530006/" class="icon"><FaFacebookF/></a>
  </div>

</div>
{/if}

<!-- svelte-ignore a11y-no-noninteractive-tabindex  a11y-click-events-have-key-events -->
<div id="navbar" tabindex="0" on:keydown={onKeyDown}>
  <div class="nav-left">
    <div id="navbar-menu-button">
      <button class="menu-button" on:click={openSideMenu}>
        <FaBars />
      </button>
    </div>

    <div class="nav-logo" id="navbar-nav-logo">
      <a href="https://visitory.io/{($locale.split('-')[0])}">
          <img alt="Visitory" src={visitoryBlackLogoUrl}/>
      </a>
    </div>

    <div class="presentation-mode-header">
      <ToggleSwitch bind:checked={hideVisualizationHeaders}>
        <p class="presentation-switch-text" slot="switch-text">{$t("Presentation mode")}</p>
      </ToggleSwitch>
    </div>
  </div>

  <div class="nav-right">
    {#if resetConfirmation}
    <div class="reset-container">
      <p id="reset-text">{$t("Reset page?")}</p>
      <div class="reset-wrapper" id="navbar-reset-wrapper-confirmation" tabindex="0">
        <div class="confirm" id="reset-page-confirm" tabindex="0" on:click={confirmReset}><IoMdCheckmark/></div>
        <div class="cancel" id="reset-page-cancel" tabindex="0" on:click={cancelReset}><IoMdClose/></div>
      </div>
    </div>
    {:else}
    <div class="reset-container">
      <div class="reset-wrapper" id="navbar-reset-wrapper-button">
        <button class="reset-button" id="reset-button" on:click={() => {resetConfirmation = true}} title="{$t("Reset page")}">{$t("Reset page")}</button>
      </div>
    </div>
    {/if}
    
    <div class="view-selector" id="navbar-view-selector">
      <ViewsDropdown
        on:loadView={loadView}
      >
        <div class="view-list" slot="icon" id="view-list">
          <div class="icon">
            <FaDatabase />
          </div>
            {$t("Views")}
          <div class="icon-down">
            <FaSortDown />
          </div>
        </div>
      </ViewsDropdown>
    </div>

    <div class="language-selector" id="navbar-language-selector">
      <IconDropdown
        options={Languages}
        handler={changeLanguageHandler}
        title="Language"
      >
        <div class="change-language" slot="icon" id="change-language">
          <div class="icon">
            <MdLanguage />
          </div>
            {Languages[$locale]}
          <div class="icon-down">
            <FaSortDown />
          </div>
        </div>
      </IconDropdown>
    </div>
    <div tabindex="0" id="app-logout">
      <a href="https://visitory.io/{($locale.split('-')[0])}/account/logout/" class="logout">
        <div class="logout-button">
          <div class="icon-wrapper">
            <FaSignOutAlt/>
          </div>
          {$t("Logout")} 
        </div>
      </a>
    </div>
  </div>
</div>

  {#if dictOpen}
    <div class="dictionary-overlay" aria-hidden="true" on:click={openDictionaryHandler} on:wheel={overlayWheel} on:touchstart={overlayTouchStart} on:touchmove={overlayTouchMove}></div>
  <aside id="dictionary" class:expanded={dictionaryExpanded} aria-label={$t("Dictionary")}> 
    <div class="dictionary-header">
      <div class="dictionary-title-row">
        <p>{$t("Dictionary")}</p>
        <div class="dictionary-actions">
          <button
            class="dictionary-expand"
            type="button"
            on:click={toggleDictionaryExpanded}
            aria-label={dictionaryExpanded ? $t("Compact view") : $t("Large view")}
            title={dictionaryExpanded ? $t("Compact view") : $t("Large view")}
          >
            <FaExpand />
          </button>
          <button
            class="dictionary-close"
            type="button"
            aria-label={$t("Close")}
            on:click={openDictionaryHandler}
          >
            <IoMdClose size="14" />
          </button>
        </div>
      </div>
    <div class="dictionary-search">
      <input
        type="text"
        placeholder={$t("Search")}
        class="dictionary-search-input"
        bind:value={searchQuery}
        on:input={handleDictionarySearchInput}
      />
        {#if searchQuery}
            <button
            type="button"
            class="dictionary-search-clear"
            aria-label={$t("Clear search")}
            on:click={() => dictionaryStore.setSearch("")}
            >
            <FaTimes />
        </button>
      {/if}
    </div>
  </div>
  <ul>
    {#if dictionaryLoading}
      <li style="text-align: center; padding: 2rem;">
        <p>{$t("Loading dictionary...")}</p>
      </li>
    {:else if filteredEntries.length === 0 && searchQuery}
      <li style="text-align: center; padding: 2rem;">
        <p>{$t("No results found")}</p>
      </li>
    {:else if dictionaryEntries.length === 0}
      <li style="text-align: center; padding: 2rem;">
        <p>{$t("Dictionary not available")}</p>
      </li>
    {:else}
      {#each filteredEntries as result}
        <li>
          <h3>
            {#each getHighlightParts(String(result.entry[`concept_${currentLang}`] || ""), searchTokens) as part}
              <span class:highlight={part.matched}>{part.text}</span>
            {/each}
          </h3>
          <p>
            {#each getHighlightParts(String(result.entry[`definition_${currentLang}`] || ""), searchTokens) as part}
              <span class:highlight={part.matched}>{part.text}</span>
            {/each}
          </p>
          {#if !result.hasNonKeywordMatch && result.matchedKeywords && result.matchedKeywords.length}
            <div class="keyword-pill-wrapper">
              {#each result.matchedKeywords as kw}
                <div class="keyword-pill">
                  {#each getHighlightParts(String(kw), searchTokens) as part}
                    <span class:highlight={part.matched}>{part.text}</span>
                  {/each}
                </div>
              {/each}
            </div>
          {/if}
        </li>
      {/each}
    {/if}
  </ul>
</aside>
{/if}

<main>

  <div class="main-content">
    <FloatingButton on:toggleDrawer={handleToggleDrawer} visible={hideVisualizationHeaders} />
    <FeedbackDrawer dataSource={visitorySource} visible={showFeedbackDrawer} onClose={handleToggleDrawer} />

    <h1>
      <img
        alt="Visitory Dashboard"
        src={visitoryLogoUrl}
        width="300"
        height="80"
      />
    </h1>

    <!--Button only for DEV purposes-->
    <!--
    <button style="color: black; background-color: white; width: 100px; position: absolute;" on:click={getVisualisationInfo}>Save View</button>
    -->

    <div class="visualizations">
      <!-- Visualizations located here -->
    </div>

    <!-- svelte-ignore a11y-no-noninteractive-tabindex -->
    <div class="add-visualization-container" id="add-visualization-container" tabindex="0" on:keydown={onKeyDown}>
      <IconDropdown
        options={Visualizations}
        handler={addVisualizationHandler}
        title={$t("New visualization")}
      >
        <div class="add-visualization" slot="icon">
          <div class="icon">
            <FaPlusSquare />
          </div>
          {$t("New visualization")}
        </div>
      </IconDropdown>
      <button
        class="dictionary-button"
        class:open={dictOpen}
        type="button"
        on:click={openDictionaryHandler}
      >
        <span class="dictionary-icon"><FaInfoCircle /></span>
        {$t("Dictionary")}
      </button>
    </div>
  </div>

  <footer class="footer">
    <!-- svelte-ignore a11y-no-noninteractive-tabindex -->
    <div class="footer-content" id="footer-content" tabindex="0" on:keydown={onKeyDown}>
      <div class="footer-logo">
        <a href="https://visitory.io/{($locale.split('-')[0])}">
            <img alt="Visitory" src={visitoryLogoUrl}/>
        </a>
      </div>

      <ul class="footer-button-list">
        <li>
          <a href="https://visitory.io/{($locale.split('-')[0])}/">{$t("Home")}</a>
        </li>
        <li>
          <a href="https://visitory.io/{($locale.split('-')[0])}/about/">{$t("About")}</a>
        </li>
        <li>
          <a href="https://visitory.io/{($locale.split('-')[0])}/regions/">{$t("Regions")}</a>
        </li>
        <li>
          <a href="https://www.insights.visitory.io/{($locale.split('-')[0])}">{$t("Blog")}</a>
        </li>
        <li>
          <a href="https://www.insights.visitory.io/{($locale.split('-')[0])}/tourismresearch">{$t("FAQ")}</a>
        </li>
        <li>
          <a href="https://visitory.io/{($locale.split('-')[0])}/contact/">{$t("Contact")}</a>
        </li>
        <li>
          <a href="https://visitory.io/{($locale.split('-')[0])}/privacy/">{$t("Privacy policy")}</a>
        </li>
      </ul>

      <div class="footer-icons" id="footer-icons">
        <a href="https://www.linkedin.com/showcase/visitory" class="icon"><FaLinkedin/></a>
        <a href="https://twitter.com/visitory_io/" class="icon"><FaTwitter/></a>
        <a href="https://www.facebook.com/Visitory-100600242530006/" class="icon"><FaFacebookF/></a>
      </div>

      <h1 id="brand-text">Visitory 2019-{new Date().getFullYear()}</h1>

    </div>
  </footer>
</main>


<style lang="scss">
  @import "./media-queries";

  .gray-screen {
    position: fixed;
    width: 100%;
    height: 100%;
    overflow: hidden;
    background-color: rgba(68, 68, 68, 0.5);
    z-index: 9000;
  }

  #side-menu {
    position: fixed;
    overflow-y: scroll;
    display: flex;
    flex-direction: column;
    background-color: var(--color-white);
    width: 210px;
    top: 0;
    bottom: 0;
    left: 0;
    z-index: 9999;
    padding: 30px;
    border-right: 0.5px solid var(--color-black);
    align-items: center;
    

    .close-icon-wrapper {
      position: absolute;
      width: 17px;
      color: var(--color-gray);
      display: flex;
      justify-content: flex-end;
      left: 1.9rem; 
      cursor: pointer;

      transition: ease-in-out 0.1s;
      &:hover{
        color: var(--color-black);
      }
    }

    .side-list {
      padding: 0;
      text-transform: uppercase;
      list-style-type: none;
      text-align: center;
      margin-bottom: 3rem;

      .sublist_button {
        display: inline-flex;
        align-items: center;
        justify-content: space-around;
        padding: 15px 0;
        text-transform: uppercase;
        font-weight: bold;
        cursor: pointer;
        color: black;
      }

      .sublist {
        padding: 0.25rem 1.5rem;
        font-size: 14px;
        text-transform: none;
        max-height: 0;
        overflow: hidden;
        transition: max-height 0.5s ease-in-out;
      }

      .open {
        max-height: 15rem; /* Set to a value large enough to accommodate the content */
      }

      .icon-down{
        margin: 0px 0px 0px 10px;
        width: 10px;
      }

      a {
        display: block;
        color: var(--color-black);
        padding: 15px 0;
        text-decoration: none;
        font-weight: 700;
      }
    }

    .logout {
      position: relative;
      display: flex;
      align-items: center;
      text-align: center;
      padding: 0.5rem 1rem;
      color: var(--color-black);
      font-weight: bold;
      text-decoration: none;
      justify-content: center;
      cursor: pointer;

      .icon-wrapper {
        margin-right: 0.5rem;
        width: 15px;
        height: 20px;
      }
    }

    .presentation-mode-side-menu {
      margin-bottom: 15px;
    }

    @media screen and (min-width: 768px) {
      .presentation-mode-side-menu {
        display: none;
      }
    }

    .presentation-switch-text {
      color: black;
      font-size: 0.95rem;
      font-weight: 600;
      white-space: nowrap;
    }

    .language-selector {
      //background-color: var(--color-white);
      //border: 2px solid var(--color-primary);
      border-radius: 0.4rem;
      position: relative;
      top: 0;
      right: 0;
      margin-bottom: 20px;

      .change-language {
        color: var(--color-black);
        display: flex;
        flex-direction: row;
        padding: 5px 10px 5px 10px;
        align-items: center;
        font-weight: bold;

        .icon {
          margin: 0px 10px 0px 0px;
        }

        .icon-down{
          margin: 0px 0px 0px 10px;
          width: 10px;
        }
      }
      :global(ul) {
          // use parent's width
          margin: 0 !important;
          right: -1px;

          color: var(--color-black);
          background-color: var(--color-white);
        }
        // When hover change color and background-color with ease
        transition: color 0.2s ease, background-color 0.2s ease;
        &:hover {
          background-color: var(--color-primary); 
          #change-language {
            color: var(--color-white);
          }
        }
    }

    .side-menu-icons {
      display: flex;
      margin-top: 50px;
      gap: 40px;

      .icon {
        width: 22px;
        height: 22px;
        color: var(--color-black);
        cursor: pointer;
        transition: ease-in-out 0.1s;
        
        &:hover{
          color: var(--color-gray);
        }
      }
    }
  }
  
  #navbar {
    position: fixed;
    display: flex;
    justify-content: space-between;
    background-color: var(--color-white);
    height: 70px;
    width: 100vw;
    z-index: 999;

    .nav-left, .nav-right {
      display: flex;
      flex-direction: row;
      align-items: center;
      width: wrap;
    }

    .nav-left {
      justify-content: flex-start;
    }

    .nav-right {
      justify-content: flex-end;
      gap: 1.5rem;
      padding-right: 1rem;

      @media screen and (max-width: 768px) {
        padding-right: 0;
      }
    }

    @media screen and (max-width: 768px) {
      .presentation-mode-header {
        display: none;
      }
      }

    .presentation-switch-text {
      color: black;
      font-size: 1.05rem;
      font-weight: 600;
    }

    .menu-button {  
      background-color: transparent;
      border: none;
      color: var(--color-black);
      cursor: pointer;
      width: 20px;
      height: 30px;
      margin: 5px 0 0 30px;
    }

    @media screen and (max-width: 768px) {
      .menu-button {
        display: flex;
        justify-content: flex-start;
      }
    }

    .nav-logo {
      position: relative;
      height: 60px;
      width: 150px;
      display: flex;
      align-items: center;
      margin-left: 2rem;

      a {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
      }
    }

    @media screen and (max-width: 768px) {
      .nav-logo {
        height: 35px;
        width: 130px;
        justify-content: flex-start;
        margin-left: 1.5rem;
      }
    }

    .logout {
      display: flex;
      align-items: center;
      text-align: center;
      padding: 0.5rem 1rem;
      color: var(--color-black);
      font-weight: bold;
      text-decoration: none;
      cursor: pointer;

      @media screen and (max-width: 768px) {
        display: none;
      }

      .icon-wrapper {
        margin-right: 0.5rem;
        width: 15px;
        height: 20px;
      }

      .logout-button {
        display: flex;
        align-items: center;
        cursor: pointer;
      }
    }
    .language-selector {
      background-color: var(--color-white);
      border-radius: 0.4rem;
      right: 12rem;
      top: 1.1rem;

      @media screen and (max-width: 768px) {
        display: none;
      }

      .change-language {
        color: var(--color-black);
        display: flex;
        flex-direction: row;
        padding: 5px 10px 5px 10px;
        align-items: center;
        font-weight: bold;

        .icon {
          margin: 0px 10px 0px 0px;
        }

        .icon-down{
          margin: 0px 0px 0px 10px;
          width: 10px;
        }
      }
      :global(ul) {
          // use parent's width
          margin: 0 !important;
          right: -1px;

          color: var(--color-black);
          background-color: var(--color-white);
        }
        // When hover change color and background-color with ease
        transition: color 0.2s ease, background-color 0.2s ease;
        &:hover {
          background-color: var(--color-primary); 
          #change-language {
            color: var(--color-white);
          }
        }
    }

    .view-selector {
      background-color: var(--color-white);
      border-radius: 0.4rem;


      .view-list {
        color: var(--color-black);
        display: flex;
        flex-direction: row;
        padding: 5px 10px 5px 10px;
        align-items: center;
        font-weight: bold;

        .icon {
          margin: 0px 10px 0px 0px;
        }

        .icon-down{
          margin: 0px 0px 0px 10px;
          width: 10px;
        }
      }
      :global(ul) {
          // use parent's width
          margin: 0 !important;
          right: -1px;

          color: var(--color-black);
          background-color: var(--color-white);
        }
        // When hover change color and background-color with ease
        transition: color 0.2s ease, background-color 0.2s ease;
        &:hover {
          background-color: var(--color-primary); 
          #view-list {
            color: var(--color-white);
          }
        }
    }
  }

  .dictionary-overlay {
    position: fixed;
    inset: 0;
    background-color: rgba(0, 0, 0, 0);
    z-index: 995;
    cursor: default;
    touch-action: auto;
  }

  #dictionary {
    background-color: rgba(0, 0, 0, 0.85);
    width: 300px;
    height: calc(100svh - 70px); // Navbar compensated height
    margin-top: 70px; // Compensate for the navbar
    position: fixed;
    right: 0;
    top: 0;
    z-index: 998;
    overflow-y: scroll;

    box-sizing: border-box;
    padding: 0 16px 16px;

    &.expanded {
      background-color: rgba(0, 0, 0, 1);
      width: min(90vw, 900px);
      height: min(85vh, 760px);
      max-height: 90vh;
      top: calc(50% + 35px);
      left: 50%;
      right: auto;
      bottom: auto;
      transform: translate(-50%, -50%);
      margin: 0;
      border-radius: 1rem;
      overflow-y: auto;
      box-shadow: 0 24px 60px rgba(0, 0, 0, 0.35);
    }

    .dictionary-header {
      position: sticky;
      top: 0;
      display: flex;
      flex-direction: column;
      margin: 0 -16px;
      padding: 8px 16px;
      background-color: rgba(0, 0, 0, 0.9);
      border-bottom: 1px solid rgba(255, 255, 255, 0.2);
      z-index: 10;

      .dictionary-title-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 0.5rem;
      }

      .dictionary-actions {
        display: flex;
        gap: 0.5rem;
        align-items: center;
      }

      .dictionary-expand {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        background: none;
        border: none;
        color: rgba(255, 255, 255, 0.7);
        padding: 0.35rem;
        font-size: 0.8rem;
        cursor: pointer;
      }

      .dictionary-expand :global(svg) {
        width: 1rem;
        height: 1rem;
      }

      .dictionary-expand:hover {
        color: rgba(255, 255, 255, 0.9);
      }

      p {
        margin: 0;
        color: var(--color-white);
        font-size: 1.25rem;
        font-weight: 700;
      }
    }

    .dictionary-close {
      background-color: rgba(255, 255, 255, 0.1);
      border: 1px solid rgba(255, 255, 255, 0.2);
      color: var(--color-white);
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 2rem;
      height: 2rem;
      padding: 0;
      border-radius: 999px;
      cursor: pointer;
      line-height: 0;
      box-sizing: border-box;

      :global(svg) {
        width: 1rem;
        height: 1rem;
        display: block;
      }
    }

    .dictionary-close:focus-visible {
      outline: 1px solid var(--color-white);
      outline-offset: 2px;
    }

    .dictionary-search {
        position: relative;
      .dictionary-search-input {
        width: 100%;
        padding: 0.5rem 2.4rem 0.5rem 0.5rem;
        background-color: rgba(255, 255, 255, 0.1);
        border: 1px solid rgba(255, 255, 255, 0.2);
        border-radius: 4px;
        color: var(--color-white);
        font-size: 0.9rem;
        box-sizing: border-box;

        &::placeholder {
          color: rgba(255, 255, 255, 0.5);
        }

        &:focus {
          outline: none;
          border-color: var(--color-primary);
          background-color: rgba(255, 255, 255, 0.15);
        }
      }
      .dictionary-search-clear {
        position: absolute;
        right: 0.45rem;
        top: 50%;
        transform: translateY(-50%);
        background: none;
        border: none;
        color: rgba(255, 255, 255, 0.7);
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 1.75rem;
        height: 1.75rem;
        padding: 0;
        cursor: pointer;
      }

      .dictionary-search-clear :global(svg) {
        width: 0.9rem;
        height: 0.9rem;
      }
    }

    ul {
      padding: 0;
      margin: 1rem 0 0;
      list-style: none;

      li {
        margin: 0 0 1.5rem;

        h3 {
          margin: 0 0 0.35rem;
          color: var(--color-primary);
          font-size: 1rem;
          font-weight: 700;
        }

        p {
          margin: 0;
          color: var(--color-white);
          line-height: 1.4;
          white-space: pre-line;
        }
      }
    }
  }

  main {
    width: 100vw;
    padding: 0;
    margin: 0;
    
    display: flex;
    flex-direction: column;
    align-items: center;
    

    @include size(tablet) {
      padding-left: 0;
      padding-right: 0;
    }

    .main-content{
      width: var(--app-width, 1200px);
      box-sizing: border-box;
      display: flex;
      flex-direction: column;
      margin-top: 70px; // Compensate for the navbar
      padding-bottom: 8rem; // leave space for the "add visualization" button
      row-gap: 4rem;

      .visualizations {
        display: flex;
        flex-wrap: wrap;
        justify-content: flex-start;
        row-gap: 2.813rem;
        column-gap: 0.9615%;
        flex-direction: row;
      }

      .add-visualization-container {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 1rem;
        flex-wrap: wrap;

        .add-visualization {
          display: flex;
          align-items: center;
          gap: 0.5rem;

          padding: 0.75rem 1.5rem;
          line-height: 1;

          background-color: var(--color-primary);
          border-radius: 0.5rem;

          user-select: none;
        }

        .dictionary-button {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          padding: 0.75rem 1.5rem;
          line-height: 1;
          border-radius: 0.5rem;
          border: 1px solid rgba(0, 0, 0, 0.1);
          background-color: var(--color-gray);
          color: var(--color-white);
          font-weight: 600;
          cursor: pointer;
          transition: background-color 0.2s ease, color 0.2s ease;
        }

        .dictionary-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 1rem;
          height: 1rem;
        }

        .dictionary-icon :global(svg) {
          width: 1rem;
          height: 1rem;
        }

        .dictionary-button.open {
          background-color: #4d4d4d;
          border-color: rgba(0, 0, 0, 0.25);
        }

        :global(ul) {
          // use parent's width
          margin: 0;
          left: 0;
          right: 0;

          color: var(--color-white);
          background-color: var(--color-black);
        }
      }
    }

    h1 {
      flex-basis: 100%;
      text-align: center;
    }
  }

  .reset-container {
    display: flex;
    flex-direction: row;
    width: auto;
    height: auto;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;

    @media screen and (max-width: 975px) { // none standard width, but needed for the reset button
      margin-right: 1rem;
      display: none;
    }

    #reset-text {
      color: var(--color-black);

      @media screen and (max-width: 768px) {
        display: none;
      }
    }
    
    &.side-menu {
      display: none;
      @media screen and (max-width: 975px) {
        display: block;
        margin: 0;
        margin-bottom: 20px;
      }
    }

    .reset-wrapper {
      background-color: var(--color-white);
      border: 2px solid var(--color-primary);
      border-radius: 0.4rem;
      width: auto;
      height: 35px;
      display: flex;
      flex-direction: row;
      justify-content: space-between;

      @media screen and (max-width: 768px) {
        justify-content: flex-start;
        width: auto;
        height: auto;
      }


      .reset-button {
        cursor: pointer;
        color: var(--color-black);
        width: auto;
        min-width: 101px;
        height: 35px;
        padding: 0 0.5rem;

        // When hover change color and background-color with ease
        transition: color 0.2s ease, background-color 0.2s ease;
        &:hover {
          background-color: var(--color-primary);
          color: var(--color-white);
        }
        @media screen and (max-width: 768px) {
          font-size: 14px;
        }
      }

      .confirm {
        cursor: pointer;
        background-color: var(--color-white);
        color: var(--color-primary);
        width: 35px;
        height: 35px;
        margin-left: 0.5rem;
        margin-right: 0.5rem;

      }
      .cancel {
        cursor: pointer;
        background-color: var(--color-white);
        color: var(--color-tertiary);
        width: 35px;
        height: 35px;
        margin-right: 0.5rem;
      }

      @media screen and (max-width: 768px) {
        .cancel,
        .confirm {
          width: 35px;
          height: 35px;
        }
      }

    }
  }

  .footer {
    position: relative;
    background-color: #232124;
    border-top: solid 1px #232124;
    text-align: center;
    width: 100%;
    bottom: 0;
    left: 0;

    .footer-content {
      display: flex;
      flex-direction: column;
      width: 100%;
      padding: 40px 0px 40px 0px;
      margin: 0 auto 0 auto;
      align-items: center;

      .footer-logo {
        position: relative;
        width: 150px;
        margin-bottom: 20px;
      }

      .footer-button-list {
        display: flex;
        flex-direction: row;
        gap: 25px;

        text-transform: uppercase;
        list-style-type: none;

        a {
          display: block;
          color: var(--color-white);
          text-decoration: none;
          font-weight: 700;
        }

        @media screen and (max-width: 768px) {
          flex-wrap: wrap;
          justify-content: center;
          gap: 2vw;
          padding: 0 10vw;
        }
      }
      .footer-icons {
        display: flex;
        margin-top: 30px;
        gap: 40px;

        .icon {
          width: 22px;
          height: 22px;
          color: var(--color-white);
          cursor: pointer;
          transition: ease-in-out 0.1s;
          &:hover{
            color: var(--color-gray);
          }
        }
      }

      #brand-text {
        margin-top: 30px;
        font-size: 0.9rem;
        font-weight: bold;
        color: var(--color-white);
      }
      
    }
  }

  .highlight {
    background-color: rgba(255, 229, 100, 0.2);
    color: inherit;
    border-radius: 0.2rem;
  }

  .keyword-pill {
    display: inline-block;
    background-color: rgba(25, 25, 25, 0.8);
    color: var(--color-primary);
    font-size: 0.65rem;
    padding: 0.18rem 0.5rem;
    border-radius: 999px;
    margin-top: 0;
  }

  .keyword-pill-wrapper {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 0.4rem;
    justify-content: flex-start;
    margin-top: 0.5rem;
  }
</style>
